import { useEffect, useState } from 'react';
import { CmsSectionShell } from './CmsSectionShell.tsx';
import { CmsSectionState } from './CmsSectionState.tsx';
import { themeOf } from './cmsTheme.ts';
import { ScheduleColumnsBlock } from './ScheduleSection.tsx';
import { buildScheduleDayColumns } from '../../domain/schedule.ts';
import { useFestivalData } from '../../lib/festivalData.ts';
import { loadThursdaySchedule, loadYouthDaySchedule } from '../../lib/data.ts';
import type { Artist, DecoratedScheduleEntry, FestivalData, FestivalEvent, ScheduleData, ScheduleSourceName } from '../../lib/types.ts';
import type { ScheduleSection } from '../../lib/types.ts';

/** The two feed sources that publish separately from the regular schedule.json. */
type SeparateFeedSource = 'communityDay' | 'youthDay';

const SEPARATE_FEED_LOADERS: Record<SeparateFeedSource, () => Promise<ScheduleData>> = {
  communityDay: loadThursdaySchedule,
  youthDay: loadYouthDaySchedule,
};

/** Fixed display order: the Thursday events' columns come before the regular festival days. */
const SOURCE_DISPLAY_ORDER: readonly ScheduleSourceName[] = ['communityDay', 'youthDay', 'festival'];

/** The feeds to show, in display order: `sources` when published (multi-feed, CMS-061/CMS-062),
 *  else the legacy single `source`, else the shared festival schedule. */
function resolveSources(
  sources: readonly ScheduleSourceName[] | undefined,
  source: ScheduleSourceName | undefined,
): readonly ScheduleSourceName[] {
  const selected = sources && sources.length > 0 ? sources : [source ?? 'festival'];
  return SOURCE_DISPLAY_ORDER.filter(s => selected.includes(s));
}

/**
 * Front-page schedule section (CMS-039): wraps the existing schedule/timetable rendering
 * (shared with the bespoke /program page and CmsTimetableSection), restricted to
 * the editor-selected days. Empty/absent `selectedDays` shows all days.
 *
 * `sources` (or the legacy single `source`) picks the feeds: 'communityDay' (CMS-061) and
 * 'youthDay' (CMS-062) swap the shared festival data for Torsdag's/Ung i Midten's own
 * thursday.json/youthday.json feed. Several feeds render side by side as one merged run of day
 * columns — Torsdag/Ung i Midten first, then the festival's own days — with an extra grid column
 * on desktop when three or more days show. ScheduleBlock derives its
 * day tabs from the program's artists (see domain/schedule.ts getScheduleDays), not
 * from the entries — and these events' artists are deliberately excluded from program.json's
 * artist list (they're announced separately). So rather than touching the shared rendering, this
 * builds a small shim FestivalData per separate feed: the real data with `scheduleEntries` and
 * the artists list replaced by the feed's own entries and a day-name stub per distinct day. The
 * column builder only needs the event type, so its open/close markers come from each feed's own
 * event days — community day and Ung i Midten are both "Torsdag", and a day name alone would
 * match the wrong one.
 */
export function CmsScheduleSection({ theme, guitar, selectedDays, source, sources }: ScheduleSection) {
  const t = themeOf(theme);
  const { data, loading, error } = useFestivalData();
  const resolvedSources = resolveSources(sources, source);
  const separateFeedSources = resolvedSources.filter((s): s is SeparateFeedSource => s !== 'festival');
  // Effect/render logic keys off the joined names so a re-render with an equal list is stable.
  const separateFeedKey = separateFeedSources.join(',');

  const [feedData, setFeedData] = useState<Partial<Record<SeparateFeedSource, FestivalData>>>({});
  // Which feed set feedData currently holds, rather than a separate "is it loading" boolean.
  // Loading is then derived during render (below), which is what lets the effect avoid setting
  // state synchronously — that is a cascading render, and the React Compiler flags it.
  const [loadedKey, setLoadedKey] = useState<string | null>(null);
  const feedLoading = separateFeedSources.length > 0 && loadedKey !== separateFeedKey;

  useEffect(() => {
    const feeds = separateFeedKey === '' ? [] : (separateFeedKey.split(',') as SeparateFeedSource[]);
    if (feeds.length === 0 || !data) return;
    let cancelled = false;
    Promise.all(feeds.map(async feed => {
      const loaded = await SEPARATE_FEED_LOADERS[feed]();
      const entries: DecoratedScheduleEntry[] = loaded.entries.map(e => ({
        ...e,
        artist: data.artistById[e.artistId],
      }));
      return [feed, buildSeparateFeedDataShim(data, entries, feed)] as const;
    }))
      .then(pairs => { if (!cancelled) setFeedData(Object.fromEntries(pairs)); })
      .finally(() => { if (!cancelled) setLoadedKey(separateFeedKey); });
    return () => { cancelled = true; };
  }, [separateFeedKey, data]);

  const effectiveLoading = loading || (separateFeedSources.length > 0 && feedLoading);
  // The single-feed Torsdag/Ung i Midten section keeps its opening-hours line; in a merged view
  // each column already carries the feed's own open/close markers, so the line is dropped.
  const openingHours = resolvedSources.length === 1 && separateFeedSources.length === 1 && data
    ? formatOpeningHours(data.program.events, separateFeedSources[0]!)
    : null;

  const columns = data && !effectiveLoading
    ? resolvedSources.flatMap(src => {
      if (src === 'festival') {
        return buildScheduleDayColumns(data, { restrictToDays: selectedDays, keyPrefix: 'festival-' });
      }
      const feed = feedData[src];
      return feed
        ? buildScheduleDayColumns(feed, { restrictToDays: selectedDays, eventType: src, keyPrefix: `${src}-` })
        : [];
    })
    : [];

  return (
    <CmsSectionShell theme={theme} guitar={guitar}>
      <CmsSectionState
        loading={effectiveLoading}
        error={!!error}
        loadingLabel="Indlæser tidsplan…"
        bodyClass={t.body}
        errorMessage="Tidsplanen kunne ikke indlæses."
      >
        {data && (
          <>
            {openingHours && <p className={`text-sm mb-4 ${t.body}`}>Åbningstider: {openingHours}</p>}
            <ScheduleColumnsBlock data={data} columns={columns} theme={theme} />
          </>
        )}
      </CmsSectionState>
    </CmsSectionShell>
  );
}

/** One stub "artist" per distinct day of the separate feed, just enough for getScheduleDays'
 *  day-tab derivation. */
function buildSeparateFeedDataShim(
  base: FestivalData,
  entries: readonly DecoratedScheduleEntry[],
  source: SeparateFeedSource,
): FestivalData {
  const days = [...new Set(entries.map(e => e.festivalDayName))];
  const dayStubArtists: Artist[] = days.map(day => ({
    id: `${source}-day-${day}`,
    name: day,
    visibility: 'artist',
    festivalDayName: day,
    displayOrder: 0,
    performanceDate: null,
    announcementText: '',
  }));

  return {
    ...base,
    program: { ...base.program, artists: { artists: dayStubArtists } },
    scheduleEntries: entries,
  };
}

/** Renders "Torsdag 12:00–23:00 · Fredag 10:00–02:00" for the event's days that have opening
 *  and/or closing times configured (CMS-062). Null when the event isn't published or no day
 *  has hours set. */
function formatOpeningHours(
  events: FestivalData['program']['events'],
  source: SeparateFeedSource,
): string | null {
  const event = events?.find(e => e.type === source) as FestivalEvent | undefined;
  if (!event) return null;

  const parts = event.days
    .filter(d => d.openingTime || d.closingTime)
    .map(d => `${d.name} ${d.openingTime?.slice(0, 5) ?? '?'}–${d.closingTime?.slice(0, 5) ?? '?'}`);

  return parts.length > 0 ? parts.join(' · ') : null;
}
